import { useState, ReactNode, useId, useCallback, CSSProperties } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { ExpandLessIcon } from '@/components/icons/gen/ExpandLessIcon';
import { ExpandMoreIcon } from '@/components/icons/gen/ExpandMoreIcon';
import { TextButton } from '../TextButton/TextButton';

export interface ExpandableTextProps {
  children: ReactNode;
  className?: string;
  maxLines?: number;
  expandText?: string;
  collapseText?: string;
  defaultExpanded?: boolean;
}

export function ExpandableText({
  children,
  maxLines = 3,
  expandText,
  collapseText,
  defaultExpanded = false,
  className,
}: ExpandableTextProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const t = useTranslations();
  const textId = useId();

  const textRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;

      const checkOverflow = (element: HTMLElement) => {
        const style = window.getComputedStyle(element);
        const lineHeight = parseFloat(style.lineHeight);
        const maxHeight = Math.round(lineHeight * maxLines);

        setIsOverflowing(element.scrollHeight > maxHeight);
      };

      checkOverflow(node);

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const element = entry.target as HTMLElement;
          checkOverflow(element);
        }
      });

      resizeObserver.observe(node);

      return () => {
        resizeObserver.disconnect();
      };
    },
    [maxLines],
  );

  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  const showMoreText = expandText || t('common.expandableText.expand');
  const showLessText = collapseText || t('common.expandableText.collapse');

  return (
    <div className={clsx('flex flex-col', className)}>
      <div
        ref={textRefCallback}
        id={textId}
        style={
          {
            '--max-line': `${maxLines}`,
          } as CSSProperties
        }
        className={clsx(
          'text-small text-neutral-primary whitespace-pre-wrap [overflow-wrap:anywhere]',
          !isExpanded && `line-clamp-[var(--max-line)]`,
        )}
        aria-expanded={isExpanded}
      >
        {children}
      </div>
      {(isOverflowing || isExpanded) && (
        <TextButton
          onClick={toggleExpanded}
          size="small"
          fontWeight="bold"
          icon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          aria-controls={textId}
          aria-expanded={isExpanded}
          className="mt-4"
        >
          {isExpanded ? showLessText : showMoreText}
        </TextButton>
      )}
    </div>
  );
}
